
import { Loader2, User, Mail, Lock, Flag, Building } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useIsMobile } from "@/hooks/use-mobile";
import { Checkbox } from "@/components/ui/checkbox";
import { useLanguage } from "@/components/LanguageContext";

interface StudentRegisterFormProps {
  formData: {
    first_name: string;
    last_name: string;
    email: string;
    password: string;
    country: string;
    city: string;
    termsAccepted: boolean;
  };
  isSubmitting: boolean;
  onFormChange: (key: string, value: string | boolean) => void;
  onSubmit: (e: React.FormEvent) => void;
  onToggleForm: () => void;
}

export const StudentRegisterForm = ({
  formData,
  isSubmitting,
  onFormChange,
  onSubmit,
  onToggleForm
}: StudentRegisterFormProps) => {
  const isMobile = useIsMobile();
  const { t } = useLanguage();
  
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
        <div className="space-y-2">
          <Label htmlFor="first_name">{t('auth.register.firstNameLabel')}</Label>
          <div className="relative">
            <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              id="first_name"
              className="pl-10"
              value={formData.first_name}
              onChange={(e) => onFormChange("first_name", e.target.value)}
              required
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="last_name">{t('auth.register.lastNameLabel')}</Label>
          <div className="relative">
            <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              id="last_name"
              className="pl-10"
              value={formData.last_name}
              onChange={(e) => onFormChange("last_name", e.target.value)}
              required
            />
          </div>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="email">{t('auth.register.emailLabel')}</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            id="email"
            type="email"
            className="pl-10"
            placeholder={t('auth.register.emailPlaceholder')}
            value={formData.email}
            onChange={(e) => onFormChange("email", e.target.value)}
            required
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="password">{t('auth.register.passwordLabel')}</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            id="password"
            type="password"
            className="pl-10"
            value={formData.password}
            onChange={(e) => onFormChange("password", e.target.value)}
            required
          />
        </div>
      </div>
      <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
        <div className="space-y-2">
          <Label htmlFor="country">{t('auth.register.countryLabel')}</Label>
          <div className="relative">
            <Flag className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              id="country"
              className="pl-10"
              placeholder={t('auth.register.countryPlaceholder')}
              value={formData.country}
              onChange={(e) => onFormChange("country", e.target.value)}
              required
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="city">{t('auth.register.cityLabel')}</Label>
          <div className="relative">
            <Building className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              id="city"
              className="pl-10"
              placeholder={t('auth.register.cityPlaceholder')}
              value={formData.city}
              onChange={(e) => onFormChange("city", e.target.value)}
              required
            />
          </div>
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <Checkbox 
          id="termsAccepted" 
          checked={formData.termsAccepted} 
          onCheckedChange={(checked) => onFormChange("termsAccepted", checked === true)} 
        />
        <Label 
          htmlFor="termsAccepted" 
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          {t('auth.register.termsLabel')}
        </Label>
      </div>
      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          t('auth.register.submitButton')
        )}
      </Button>
      <div className="mt-4 text-center text-sm">
        <Button
          variant="link"
          onClick={onToggleForm}
          className="text-xs"
        >
          {t('publicProfile.hasAccount')}
        </Button>
      </div>
    </form>
  );
};
